import { ArrowLeftIcon, CalendarBlankIcon, CheckSquareIcon, PlusIcon } from '@phosphor-icons/react'
import moment from 'moment'
import { useState } from 'react'
import Button from 'app/components/Button'
import Spinner from 'app/components/Spinner'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuTrigger,
} from 'app/components/ui/dropdown-menu'
import { CardTask, isOverdue } from './useContactTasks'
import { cn } from 'app/lib/utils'
import { ContactJson } from 'app/types/entities'

/** Quick reschedules. A full date picker is heavier than this surface needs. */
const RESCHEDULE_PRESETS: { label: string; days: number }[] = [
  { label: `Today`, days: 0 },
  { label: `Tomorrow`, days: 1 },
  { label: `In 3 days`, days: 3 },
  { label: `Next week`, days: 7 },
  { label: `In 2 weeks`, days: 14 },
]

/** Due wording that leads with lateness, since that is what changes behavior. */
export function dueLabel(task: CardTask): string {
  if (!task.dueDate) return `No due date`
  const due = moment(task.dueDate)
  if (!due.isValid()) return `No due date`
  const days = due.startOf(`day`).diff(moment().startOf(`day`), `days`)
  if (days < 0) return `${Math.abs(days)} ${Math.abs(days) === 1 ? `day` : `days`} overdue`
  if (days === 0) return `Due today`
  if (days === 1) return `Due tomorrow`
  if (days < 7) return `Due ${due.format(`ddd`)}`
  return `Due ${due.format(`MMM D`)}`
}

function TaskRow({
  task,
  saving,
  onToggle,
  onReschedule,
}: {
  task: CardTask
  saving: boolean
  onToggle: (status: `open` | `closed`) => void
  onReschedule: (dueDate: string) => void
}) {
  const closed = task.status !== `open`
  const late = isOverdue(task)
  return (
    <div
      className={cn(
        `group/task flex items-start gap-2.5 rounded-lg px-2 py-2 transition-colors hover:bg-surface-subtle`,
        saving && `opacity-60`,
      )}
    >
      <button
        type="button"
        onClick={() => onToggle(closed ? `open` : `closed`)}
        aria-label={closed ? `Reopen task` : `Mark task complete`}
        className={cn(
          `mt-0.5 flex h-4 w-4 shrink-0 items-center justify-center rounded border transition-colors`,
          closed
            ? `border-green-600 bg-green-600 text-on-color`
            : `border-strong bg-surface-raised hover:border-primary-500 hover:bg-primary-50`,
        )}
      >
        {closed && <CheckSquareIcon size={11} weight="bold" />}
      </button>

      <div className="min-w-0 flex-1">
        <p className={cn(`text-body-sm`, closed ? `text-minimal line-through` : `text-strong`)}>
          {task.title || `Untitled task`}
        </p>
        {task.description && !closed && (
          <p className="mt-0.5 line-clamp-2 text-body-xs text-subtle">{task.description}</p>
        )}
        <p className={cn(`mt-0.5 text-body-xs`, late ? `text-red-600` : `text-subtle`)}>{dueLabel(task)}</p>
      </div>

      {!closed && (
        // Reveals on hover so a long list stays quiet until you reach for it.
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <button
              type="button"
              aria-label="Reschedule task"
              className="shrink-0 rounded p-1 text-minimal opacity-0 transition hover:bg-surface-muted hover:text-default focus-visible:opacity-100 group-hover/task:opacity-100"
            >
              <CalendarBlankIcon size={15} weight="bold" />
            </button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end">
            <DropdownMenuLabel className="text-body-xs font-normal uppercase tracking-wide text-subtle">
              Reschedule
            </DropdownMenuLabel>
            {RESCHEDULE_PRESETS.map((preset) => (
              <DropdownMenuItem
                key={preset.label}
                onClick={() => onReschedule(moment().add(preset.days, `days`).endOf(`day`).toISOString())}
              >
                {preset.label}
              </DropdownMenuItem>
            ))}
          </DropdownMenuContent>
        </DropdownMenu>
      )}
    </div>
  )
}

/**
 * The focus card's task face: the contact's open work, completable and
 * reschedulable in place, with finished tasks tucked below.
 */
export function TasksPanel({
  contact,
  open,
  done,
  loading,
  saving,
  active,
  onToggle,
  onReschedule,
  onNewTask,
  onBack,
}: {
  contact: ContactJson
  open: CardTask[]
  done: CardTask[]
  loading: boolean
  saving: string[]
  /** True while this face is showing. */
  active: boolean
  onToggle: (id: string, status: `open` | `closed`) => void
  onReschedule: (id: string, dueDate: string) => void
  onNewTask: () => void
  onBack: () => void
}) {
  const [showDone, setShowDone] = useState(false)
  const firstName = contact.firstName || contact.name?.split(` `)[0] || `this contact`

  return (
    <div className="flex h-full flex-col overflow-hidden rounded-2xl border border-default bg-surface-raised shadow-elevation-4">
      <div className="flex shrink-0 items-center gap-2 border-b border-subtle px-4 py-3">
        <button
          type="button"
          onClick={onBack}
          aria-label="Back to contact"
          className="rounded-lg p-1.5 text-subtle transition hover:bg-surface-muted hover:text-strong"
        >
          <ArrowLeftIcon size={16} weight="bold" />
        </button>
        <div className="min-w-0 flex-1">
          <p className="truncate text-ui-sm text-strong">Tasks</p>
          <p className="truncate text-body-xs text-subtle">
            {open.length === 0 ? `Nothing open` : `${open.length} open`}
            {done.length > 0 ? ` · ${done.length} done` : ``}
          </p>
        </div>
        <Button mode="white" size="sm" onClick={onNewTask}>
          <PlusIcon size={14} weight="bold" className="mr-1" /> New
        </Button>
      </div>

      <div className="min-h-0 flex-1 overflow-y-auto px-2 py-2">
        {loading && open.length === 0 && done.length === 0 ? (
          <div className="flex h-full items-center justify-center">
            <Spinner size={5} />
          </div>
        ) : open.length === 0 && done.length === 0 ? (
          <div className="flex h-full flex-col items-center justify-center text-center">
            <CheckSquareIcon size={28} className="text-minimal" />
            <p className="mt-2 text-body-sm text-subtle">No tasks for {firstName}.</p>
            <button type="button" onClick={onNewTask} className="mt-1 text-body-sm text-primary-600 hover:underline">
              Create one
            </button>
          </div>
        ) : (
          <>
            {open.map((task) => (
              <TaskRow
                key={task.id}
                task={task}
                saving={saving.includes(task.id)}
                onToggle={(status) => onToggle(task.id, status)}
                onReschedule={(dueDate) => onReschedule(task.id, dueDate)}
              />
            ))}

            {open.length === 0 && done.length > 0 && (
              <p className="px-2 py-6 text-center text-body-sm text-subtle">All caught up with {firstName}.</p>
            )}

            {done.length > 0 && (
              <div className="mt-1 border-t border-subtle pt-1">
                <button
                  type="button"
                  onClick={() => setShowDone((value) => !value)}
                  className="w-full px-2 py-1.5 text-left text-body-xs text-subtle transition hover:text-default"
                >
                  {showDone ? `Hide` : `Show`} {done.length} completed
                </button>
                {showDone &&
                  done.map((task) => (
                    <TaskRow
                      key={task.id}
                      task={task}
                      saving={saving.includes(task.id)}
                      onToggle={(status) => onToggle(task.id, status)}
                      onReschedule={(dueDate) => onReschedule(task.id, dueDate)}
                    />
                  ))}
              </div>
            )}
          </>
        )}
      </div>
    </div>
  )
}
